import type { ReactElement } from "react"
import { Link } from "gatsby"
import Media from "@gatsby-plugin-generic-page/components/Media"
import type { InfoBox } from "@gatsby-plugin-generic-page/fragments/contactSection"
import { Box, Text } from "@ionos-web-design-system/react"
import DynamicUdsIcon, {
  ICON_GROUP_SQUIDEX_TYPE,
} from "~/components/DynamicUdsIcon"
import { cn } from "~/helpers/cn"
import isEmpty from "~/lib/isEmpty"

export type InfoBoxCardProps = {
  box: InfoBox
  className?: string
}

const cardClassName = (
  isVisibleOnSmallOnly: boolean | undefined,
  className: string | undefined
) =>
  cn(
    "flex flex-col flex-1 min-w-0",
    isVisibleOnSmallOnly ? "md:hidden" : "",
    className
  )

function CardContent({
  box,
}: {
  box: InfoBox
}): ReactElement<{ box: InfoBox }> {
  return (
    <Box className="uds-action-quiet h-full bg-surface-base rounded-sm shadow-md">
      <div className="flex flex-col">
        {!isEmpty(box.flatData.icon) ? (
          <DynamicUdsIcon
            iconName={box.flatData.icon.iconId}
            iconGroup={ICON_GROUP_SQUIDEX_TYPE[box.flatData.icon.__typename]}
            size="medium"
          />
        ) : (
          <Media media={box.flatData.images} />
        )}

        <Text
          variant="headingXl"
          weight="black"
          color="base"
          className="mt-4 mb-1"
          asChild
        >
          <h3>{box.flatData.title}</h3>
        </Text>

        <Text variant="bodySm" color="base">
          {box.flatData.text}
        </Text>
      </div>
    </Box>
  )
}

export default function InfoBoxCard({
  box,
  className,
}: InfoBoxCardProps): ReactElement<InfoBoxCardProps> {
  const href = !isEmpty(box.flatData.href) ? box.flatData.href : undefined
  const to = !isEmpty(box.flatData.pages)
    ? box.flatData.pages[0].flatData.url
    : undefined

  if (to) {
    return (
      <Link
        to={to}
        className={cardClassName(box.flatData.isVisibleOnSmallOnly, className)}
      >
        <CardContent box={box} />
      </Link>
    )
  }

  return (
    <a
      href={href ?? "/"}
      className={cardClassName(box.flatData.isVisibleOnSmallOnly, className)}
    >
      <CardContent box={box} />
    </a>
  )
}
